import { createSoftReload, getHostConfig, getParamOrHidden } from './utils.js';
import { initLoginManagement } from './login-management.js';
import { initGuestRegistration } from './guest-registration.js';
import { initTicketRegistration } from './ticket-registration.js';
import { initTicketList, initMembershipToggle } from './ticket-list-render.js';
import { initWaitlistManagement } from './waitlist-management.js';
import { initPaymentHandling } from './payment-handling.js';
import { resizeContainer, smoothCollapse, smoothExpand } from './form-helpers.js';
import { initCheckoutEnhancements, activateEnhancements, getCheckoutContext, cleanupStickySummary } from './checkout-enhancements.js';
import { initDescriptionToggles } from './description-toggle.js';
import { getBotProtectionConfig, injectBotProtection } from './bot-protection.js';

const EXPANDED_FORM_SELECTOR = '.register-form[data-expanded], .register-guest-form[data-expanded]';
const INLINE_CONTAINER_SELECTOR = '.ticket-checkout-inline';

let scheduleExpandedFormResizeUpdate;
// Keep a reference so the observer isn't garbage collected.
let expandedFormsResizeObserver;
let deckKeyboardHandler;
let preselectionApplied = false;

function buildAccessCodeUrl(form, code) {
  const currentUrl = new URL(window.location.href);
  const redirect = form?.dataset?.redirect || '';
  const baseUrl = redirect ? new URL(redirect, currentUrl.origin) : currentUrl;

  // Preserve existing params when redirecting to a different path
  currentUrl.searchParams.forEach((value, key) => {
    if (key === 'code') {
      return;
    }
    if (!baseUrl.searchParams.has(key)) {
      baseUrl.searchParams.set(key, value);
    }
  });

  if (code) {
    baseUrl.searchParams.set('code', code);
  } else {
    baseUrl.searchParams.delete('code');
  }
  baseUrl.searchParams.delete('access_code');

  baseUrl.hash = '';
  return baseUrl;
}

function initAccessCodeForms(host) {
  if (!host) {
    return;
  }

  host.querySelectorAll('[data-access-code-form]').forEach((form) => {
    if (form.dataset.accessCodeInit === '1') {
      return;
    }
    form.dataset.accessCodeInit = '1';

    const input = form.querySelector('input[name="code"]');
    const applyCode = (raw) => {
      const cleaned = (raw || '').trim().toUpperCase();
      // Only allow alphanumeric codes (backend enforces 6-20 chars, alnum only)
      if (cleaned && !/^[A-Z0-9]+$/.test(cleaned)) {
        return;
      }
      if (input) {
        input.value = cleaned;
      }
      const targetUrl = buildAccessCodeUrl(form, cleaned);
      window.location.href = targetUrl.toString();
    };

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      applyCode(input?.value || '');
    });

    const clearBtn = form.querySelector('[data-access-code-clear]');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => applyCode(''));
    }
  });
}

function initAccessCodeToggles(host) {
  if (!host) {
    return;
  }

  host.querySelectorAll('[data-access-code-toggle]').forEach((toggle) => {
    if (toggle.dataset.accessCodeToggleInit === '1') {
      return;
    }
    toggle.dataset.accessCodeToggleInit = '1';

    const section = toggle.closest('[data-access-code-section]');
    const content = section?.querySelector('[data-access-code-content]');
    if (!content) {
      return;
    }

    const toggleContent = () => {
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!isExpanded));

      if (isExpanded) {
        smoothCollapse(content);
      } else {
        content.classList.remove('hidden');
        smoothExpand(content);
      }
    };

    toggle.addEventListener('click', toggleContent);

    toggle.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggleContent();
      }
    });
  });
}

function initDeckNavigation(host) {
  const deck = host.querySelector('#ticket-deck');
  if (!deck || deck.dataset.deckNavInit === '1') {
    return;
  }

  deck.dataset.deckNavInit = '1';
  let cards = Array.from(deck.querySelectorAll('.checkout-ticket-option'));
  const posEl = host.querySelector('#deck-pos');
  const totalEl = host.querySelector('#deck-total');
  const prev = host.querySelector('.deck-prev');
  const next = host.querySelector('.deck-next');

  const updateCounter = (cardList) => {
    if (totalEl) {
      totalEl.textContent = String(cardList.length || 0);
    }
    if (posEl && (posEl.textContent === '0' || !posEl.textContent)) {
      posEl.textContent = cardList.length > 0 ? '1' : '0';
    }
  };

  updateCounter(cards);

  const scrollToIndex = (index) => {
    if (index < 0 || index >= cards.length) {
      return;
    }
    cards[index].scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (posEl) {
      posEl.textContent = String(index + 1);
    }
  };

  if (!('IntersectionObserver' in window)) return;
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
          const idx = cards.indexOf(entry.target);
          if (idx >= 0 && posEl) {
            posEl.textContent = String(idx + 1);
          }
        }
      });
    },
    { threshold: [0.6] },
  );

  cards.forEach((card) => observer.observe(card));

  // Fallback: if cards are not yet in the DOM (deferred rendering), watch for them
  if (cards.length === 0) {
    const childObserver = new MutationObserver(() => {
      cards = Array.from(deck.querySelectorAll('.checkout-ticket-option'));
      if (cards.length > 0) {
        childObserver.disconnect();
        updateCounter(cards);
        cards.forEach((card) => observer.observe(card));
      }
    });
    childObserver.observe(deck, { childList: true, subtree: true });
  }

  if (prev && !prev.dataset.deckNavInit) {
    prev.dataset.deckNavInit = '1';
    prev.addEventListener('click', () => {
      const current = Number(posEl?.textContent || '1') - 1;
      scrollToIndex(current - 1);
    });
  }

  if (next && !next.dataset.deckNavInit) {
    next.dataset.deckNavInit = '1';
    next.addEventListener('click', () => {
      const current = Number(posEl?.textContent || '1') - 1;
      scrollToIndex(current + 1);
    });
  }

  if (deckKeyboardHandler) {
    window.removeEventListener('keydown', deckKeyboardHandler);
  }

  deckKeyboardHandler = (event) => {
    const tag = event.target?.tagName;
    if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || event.target?.isContentEditable) {
      return;
    }
    if (['ArrowDown', 'j'].includes(event.key)) {
      event.preventDefault();
      next?.click();
    }
    if (['ArrowUp', 'k'].includes(event.key)) {
      event.preventDefault();
      prev?.click();
    }
  };

  window.addEventListener('keydown', deckKeyboardHandler);
}

function initTicketToggles(host) {
  const buttons = host.querySelectorAll('.ticket-book-btn');

  buttons.forEach((button) => {
    if (button.dataset.inlineToggleInit === '1') return;
    button.dataset.inlineToggleInit = '1';

    button.addEventListener('click', () => {
      const container = findInlineCheckout(button, host);
      if (!container) return;

      const isOpen = container.classList.contains('expanded') && !container.classList.contains('hidden');

      if (isOpen) {
        closeInlineCheckout(container);
        return;
      }
      collapseOtherInlineCheckouts(host, container);
      openInlineCheckout(container);

      // Scroll zur Ticket-Card with offset for sticky headers, highlight, then focus
      const card = container.closest('article') || container.closest('.ticket-card');
      const scrollTarget = card || container;
      setTimeout(() => {
        // Account for sticky header height
        const headerHeight = document.querySelector('.sticky-header, .navbar, header')?.offsetHeight || 0;
        const targetRect = scrollTarget.getBoundingClientRect();
        const scrollTop = window.scrollY + targetRect.top - headerHeight - 16;
        window.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });

        // Subtle highlight pulse after scroll (CSS class, respects prefers-reduced-motion)
        scrollTarget.classList.add('checkout-scroll-highlight');
        setTimeout(() => {
          scrollTarget.classList.remove('checkout-scroll-highlight');
        }, 1200);

        // Focus first visible input for keyboard users (after scroll settles)
        setTimeout(() => {
          const firstInput = container.querySelector('input:not([type="hidden"]):not([type="checkbox"]), select, textarea');
          if (firstInput) firstInput.focus({ preventScroll: true });
        }, 350);
      }, 100);
    });
  });

  // Escape key: close any open inline checkout (accessibility: keyboard dismissal)
  // Skip if a focus-trapped inner panel (login, waitlist) is active — let its handler fire first
  if (!host.dataset.escapeInit) {
    host.dataset.escapeInit = '1';
    host.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        // Don't close checkout if an inner dialog/panel is open (it handles its own ESC)
        const activeInnerPanel = host.querySelector(
          '[data-login-inline]:not(.hidden), .waitlist-join-form.waitlist-form-expanded'
        );
        if (activeInnerPanel) return;

        const openCheckouts = host.querySelectorAll(`${INLINE_CONTAINER_SELECTOR}.expanded`);
        if (openCheckouts.length > 0) {
          openCheckouts.forEach((container) => closeInlineCheckout(container));
          // Return focus to the book button that opened it
          const lastBtn = host.querySelector('.ticket-book-btn:focus, .ticket-book-btn');
          if (lastBtn) lastBtn.focus();
        }
      }
    });
  }

  host.querySelectorAll('.toggle-guest-form').forEach((button) => {
    if (button.dataset.toggleInit === '1') {
      return;
    }
    button.dataset.toggleInit = '1';
    button.addEventListener('click', () => {
      const targetId = button.dataset.target;
      if (!targetId) {
        return;
      }
      const target = document.getElementById(targetId);
      if (!target) {
        return;
      }
      if (target.classList.contains('hidden')) {
        smoothExpand(target);
      } else {
        smoothCollapse(target);
      }
    });
  });

  host.querySelectorAll('.toggle-consent-btn').forEach((button) => {
    if (button.dataset.toggleInit === '1') {
      return;
    }
    button.dataset.toggleInit = '1';
    button.addEventListener('click', () => {
      const ticketId = button.dataset.ticket;
      if (!ticketId) {
        return;
      }
      const target = document.getElementById(`consent-box-${ticketId}`);
      if (target) {
        smoothExpand(target);
      }
    });
  });
}

function getPreselectTicketId() {
  try {
    const params = new URLSearchParams(window.location.search);
    const keys = ['ticket', 'ticket_id', 'ticket_type'];
    for (const key of keys) {
      const raw = params.get(key);
      if (raw != null && raw !== '') {
        return raw.trim();
      }
    }
  } catch (error) {
    // ignore
  }
  return '';
}

function escapeSelector(value) {
  if (window.CSS && typeof window.CSS.escape === 'function') {
    return window.CSS.escape(value);
  }
  return value.replace(/["\\]/g, '\\$&');
}

function isExpanded(element) {
  if (!element) {
    return false;
  }
  if (element.dataset?.expanded === '1') {
    return true;
  }
  if (element.classList.contains('hidden')) {
    return false;
  }
  return true;
}

function openInlineCheckout(container) {
  if (!container) return;
  if (container.dataset.animating) return;
  container.dataset.animating = '1';

  // CSS-class based animation (no inline styles that override CSS rules)
  container.classList.remove('hidden');
  container.dataset.expanded = '1';

  // Use requestAnimationFrame to ensure the browser has rendered the un-hidden state
  // before adding the expanded class, enabling the CSS transition
  requestAnimationFrame(() => {
    container.classList.add('expanded');
  });

  // After expand animation completes: release overflow constraint so
  // dynamically-added content (group rows, plus-ones) is never clipped
  setTimeout(() => {
    if (container.classList.contains('expanded')) {
      container.classList.add('settled');
    }
    delete container.dataset.animating;

    // Move focus to first interactive element for keyboard/screen-reader users
    const firstInput = container.querySelector('input:not([type="hidden"]), select, textarea');
    if (firstInput) firstInput.focus({ preventScroll: false });
  }, 400);

  // Clear any residual inline styles on forms inside the container
  // (may be left over from previous smoothExpand/smoothCollapse animations)
  const forms = container.querySelectorAll('.register-form, .register-guest-form');
  forms.forEach((form) => {
    form.style.maxHeight = '';
    form.style.opacity = '';
    form.dataset.expanded = '1';
  });

  // Deferred checkout enhancements: activate DOM-heavy work (buildEnhancements,
  // attachListeners, refreshSummary) only when the form becomes visible.
  // With 187 ticket types this avoids ~11,000 reflows for hidden forms.
  forms.forEach((form) => {
    const ctx = getCheckoutContext(form);
    if (ctx && !ctx._activated) {
      activateEnhancements(ctx);
    }
  });

  // Deferred membership toggle: init only for forms in this container
  initMembershipToggle(container);

  // Deferred bot protection: inject widget only when form becomes visible.
  // This avoids N parallel API calls + DOM injections for hidden forms on page load.
  // The submit handler in guest-registration.js / ticket-registration.js has its own
  // getBotProtectionConfig fallback, so this is an early-inject for Turnstile/reCAPTCHA
  // widgets that need user interaction before submit.
  const hostSection = container.closest('section[data-event-name]');
  const eventName = hostSection?.dataset.eventName;
  if (eventName) {
    forms.forEach((form) => {
      if (form.dataset.botProtectionInit === '1') return;
      const inviteToken = getParamOrHidden(form, ['invite', 'invite_token']);
      const inviteCode = getParamOrHidden(form, 'invite_code');
      getBotProtectionConfig(eventName, { inviteToken, inviteCode })
        .then((cfg) => injectBotProtection(form, cfg))
        .catch(() => { /* submit handler will retry */ });
    });
  }
}

function closeInlineCheckout(container) {
  if (!container) return;
  if (container.dataset.animating) return;
  container.dataset.animating = '1';

  delete container.dataset.expanded;

  // Clean up sticky summary bars to prevent memory leaks
  const forms = container.querySelectorAll('.register-form, .register-guest-form');
  forms.forEach((form) => {
    delete form.dataset.expanded;
    const ctx = getCheckoutContext(form);
    if (ctx) cleanupStickySummary(ctx);
  });

  // Already hidden - just ensure clean state
  if (container.classList.contains('hidden')) {
    container.classList.remove('expanded', 'settled');
    delete container.dataset.animating;
    return;
  }

  // Remove settled first so overflow:hidden is restored for collapse animation
  container.classList.remove('settled');

  // Set current scrollHeight as explicit start value for a smooth collapse
  // (avoids animating through thousands of px of empty space)
  container.style.maxHeight = container.scrollHeight + 'px';
  requestAnimationFrame(() => {
    container.classList.remove('expanded');
    container.style.maxHeight = '';
  });

  // Add hidden class after transition completes
  const onTransitionEnd = (e) => {
    if (e && e.target !== container) return;
    container.removeEventListener('transitionend', onTransitionEnd);
    clearTimeout(fallbackTimer);
    container.classList.add('hidden');
    delete container.dataset.animating;
  };
  container.addEventListener('transitionend', onTransitionEnd);
  // Fallback in case transitionend doesn't fire (e.g. display:none race)
  const fallbackTimer = setTimeout(onTransitionEnd, 400);
}

function findInlineCheckout(button, host) {
  if (!button || !host) {
    return null;
  }
  const ticketId = button.dataset.ticketId || '';
  const escapedId = ticketId ? escapeSelector(ticketId) : '';
  const card = button.closest('.ticket-card')
    || button.closest('article')
    || button.closest('.ticket-row')
    || button.closest('.ticket-item');
  if (card) {
    const scoped = escapedId
      ? card.querySelector(`${INLINE_CONTAINER_SELECTOR}[data-ticket-id="${escapedId}"]`)
      : card.querySelector(INLINE_CONTAINER_SELECTOR);
    if (scoped) {
      return scoped;
    }
  }
  if (escapedId) {
    return host.querySelector(`${INLINE_CONTAINER_SELECTOR}[data-ticket-id="${escapedId}"]`);
  }
  return host.querySelector(INLINE_CONTAINER_SELECTOR);
}

function collapseOtherInlineCheckouts(host, activeContainer) {
  if (!host) {
    return;
  }
  host.querySelectorAll(`${INLINE_CONTAINER_SELECTOR}.expanded`).forEach((container) => {
    if (container !== activeContainer) {
      closeInlineCheckout(container);
    }
  });
}

function preselectTicket(host) {
  if (preselectionApplied) {
    return;
  }
  const ticketId = getPreselectTicketId();
  if (!ticketId) {
    return;
  }

  const root = host || document;
  const escapedId = escapeSelector(ticketId);

  const inlineContainer = root.querySelector(`.ticket-checkout-inline[data-ticket-id="${escapedId}"]`);
  if (inlineContainer) {
    const bookBtn = root.querySelector(`.ticket-book-btn[data-ticket-id="${escapedId}"]`);
    if (!inlineContainer.classList.contains('expanded') && bookBtn) {
      bookBtn.click();
    }
    inlineContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
    preselectionApplied = true;
    return;
  }

  const consentBtn = root.querySelector(`.toggle-consent-btn[data-ticket="${escapedId}"]`);
  const consentFormId = consentBtn?.dataset.formId || '';
  const consentForm = consentFormId ? document.getElementById(consentFormId) : null;
  if (consentForm || consentBtn) {
    if (consentForm && !isExpanded(consentForm)) {
      if (consentBtn) {
        consentBtn.click();
      } else {
        smoothExpand(consentForm);
      }
    }
    (consentForm || consentBtn)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    preselectionApplied = true;
    return;
  }

  const guestBtn = root.querySelector(`.toggle-guest-form[data-ticket="${escapedId}"]`);
  const guestFormId = guestBtn?.dataset.target || '';
  const guestForm = guestFormId ? document.getElementById(guestFormId) : null;
  if (guestForm || guestBtn) {
    if (guestForm && !isExpanded(guestForm)) {
      if (guestBtn) {
        guestBtn.click();
      } else {
        smoothExpand(guestForm);
      }
    }
    (guestForm || guestBtn)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    preselectionApplied = true;
  }
}


function ensureExpandedFormResizeWatcher() {
  if (scheduleExpandedFormResizeUpdate) {
    scheduleExpandedFormResizeUpdate();
    return;
  }

  let resizeRaf = null;

  scheduleExpandedFormResizeUpdate = () => {
    if (resizeRaf) {
      cancelAnimationFrame(resizeRaf);
    }
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = null;
      document
        .querySelectorAll(EXPANDED_FORM_SELECTOR)
        .forEach((element) => resizeContainer(element));
    });
  };

  if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
    expandedFormsResizeObserver = new ResizeObserver(() => {
      scheduleExpandedFormResizeUpdate();
    });
    expandedFormsResizeObserver.observe(document.documentElement || document.body);
  } else if (typeof window !== 'undefined') {
    window.addEventListener('resize', () => {
      scheduleExpandedFormResizeUpdate();
    });
  }

  scheduleExpandedFormResizeUpdate();
}

export function initTicketsPage() {
  let host = document.querySelector('section#tickets');
  if (!host) return;

  let config = getHostConfig(host);
  let softReload = () => {};

  // Shared error guard – hoisted so reinitialize, boot, and onReplace can all use it
  const safe = (name, fn) => {
    try { fn(); } catch (e) { console.error(`[tickets-init] ${name} failed:`, e); }
  };

  const reinitialize = (target) => {
    const scope = target || host;
    safe('initAccessCodeForms', () => initAccessCodeForms(scope));
    safe('initAccessCodeToggles', () => initAccessCodeToggles(scope));

    let loginManager = null;
    safe('initLoginManagement', () => {
      loginManager = initLoginManagement(scope, { triggerSoftReload: () => softReload() });
    });

    safe('initTicketList', () => initTicketList(scope, { softReload }));
    safe('initWaitlistManagement', () => initWaitlistManagement(scope, {
      openInlineLogin: loginManager?.openInlineLogin,
    }));
    safe('initCheckoutEnhancements', () => initCheckoutEnhancements(scope, config, {
      openInlineLogin: loginManager?.openInlineLogin,
    }));

    if (loginManager) {
      safe('initGuestRegistration', () => initGuestRegistration(scope, config, {
        attachGuestGate: loginManager.attachGuestGate,
        openInlineLogin: loginManager.openInlineLogin,
      }));
    }

    safe('initTicketRegistration', () => initTicketRegistration(scope, config, { softReload }));
    safe('initDeckNavigation', () => initDeckNavigation(scope));
    safe('initTicketToggles', () => initTicketToggles(scope));
    safe('initDescriptionToggles', () => initDescriptionToggles(scope));
    if (scheduleExpandedFormResizeUpdate) {
      scheduleExpandedFormResizeUpdate();
    }
  };

  softReload = createSoftReload({
    selector: 'section#tickets',
    onReplace: (fresh) => {
      host = fresh;
      config = getHostConfig(fresh);
      reinitialize(fresh);
      safe('initPaymentHandling', () => initPaymentHandling({ host: fresh, config }));
      safe('preselectTicket', () => preselectTicket(fresh));
    },
  });

  const boot = () => {
    safe('ensureExpandedFormResizeWatcher', () => ensureExpandedFormResizeWatcher());
    reinitialize(host);
    safe('initPaymentHandling', () => initPaymentHandling({ host, config }));
    safe('preselectTicket', () => preselectTicket(host));

    // #933 FIX: Scroll to ticket section after access code entry
    // When user submits an access code, the page reloads with ?code=XXX.
    // Scroll to the tickets section so hidden tickets are immediately visible.
    safe('scrollAfterAccessCode', () => {
      const urlParams = new URLSearchParams(window.location.search);
      const hasCode = urlParams.has('code') || urlParams.has('access_code');
      if (hasCode && host) {
        // Use requestAnimationFrame to ensure DOM is fully laid out
        requestAnimationFrame(() => {
          host.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
}
